import { useState } from 'react';
import { motion } from 'framer-motion';

const BeforeAfterSlider = ({ beforeImage, afterImage, title, objectPosition = 'center' }) => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);

  const handleMove = (e) => {
    if (!isDragging) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    const percent = Math.max(0, Math.min((x / rect.width) * 100, 100));
    setSliderPosition(percent);
  };

  const handleTouchMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.touches[0].clientX - rect.left, rect.width));
    const percent = Math.max(0, Math.min((x / rect.width) * 100, 100));
    setSliderPosition(percent);
  };

  return (
    <div className="relative">
      <h3 className="text-lg font-outfit font-semibold text-slate-800 mb-3 text-center">{title}</h3>
      <div
        className="relative w-full aspect-square rounded-2xl overflow-hidden cursor-ew-resize select-none shadow-lg"
        onMouseMove={handleMove}
        onMouseDown={() => setIsDragging(true)}
        onMouseUp={() => setIsDragging(false)}
        onMouseLeave={() => setIsDragging(false)}
        onTouchMove={handleTouchMove}
        data-testid={`before-after-${title.toLowerCase().replace(' ', '-')}`}
      >
        {/* After Image (Background) */}
        <img
          src={afterImage}
          alt={`${title} - After cleaning`}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition }}
          draggable={false}
        />
        
        {/* Before Image (Clipped) */}
        <div
          className="absolute inset-0 overflow-hidden"
          style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
        >
          <img
            src={beforeImage}
            alt={`${title} - Before cleaning`}
            className="absolute inset-0 w-full h-full object-cover"
            style={{ objectPosition }}
            draggable={false}
          />
        </div>
        
        {/* Slider Line */}
        <div
          className="absolute top-0 bottom-0 w-1 bg-white shadow-lg cursor-ew-resize z-10"
          style={{ left: `calc(${sliderPosition}% - 2px)` }}
        >
          {/* Slider Handle */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 
                          w-10 h-10 bg-white rounded-full shadow-xl flex items-center justify-center
                          border-4 border-sky-500">
            <div className="flex gap-0.5">
              <div className="w-0.5 h-4 bg-sky-500 rounded-full" />
              <div className="w-0.5 h-4 bg-sky-500 rounded-full" />
            </div>
          </div>
        </div>
        
        {/* Labels */}
        <div className="absolute top-3 left-3 bg-pink-500 text-white text-xs font-semibold px-3 py-1.5 rounded-full shadow-lg">
          BEFORE
        </div>
        <div className="absolute top-3 right-3 bg-sky-500 text-white text-xs font-semibold px-3 py-1.5 rounded-full shadow-lg">
          AFTER
        </div>
      </div>
      <p className="text-center text-slate-500 text-sm mt-2">Drag to compare</p>
    </div>
  );
};

export const BeforeAfterGallery = () => {
  const comparisons = [
    {
      title: "Bathroom Deep Clean",
      beforeImage: "https://customer-assets.emergentagent.com/job_family-cleaning-kc/artifacts/lcxa0eb4_8342628869136602214.jpeg",
      afterImage: "https://customer-assets.emergentagent.com/job_family-cleaning-kc/artifacts/piwsg72l_7724432239370976162.jpeg",
      objectPosition: "left bottom"
    },
    {
      title: "Refrigerator Cleaning",
      beforeImage: "https://customer-assets.emergentagent.com/job_family-cleaning-kc/artifacts/s1i4hurr_4644727248647143417.jpeg",
      afterImage: "https://customer-assets.emergentagent.com/job_family-cleaning-kc/artifacts/rrxrqjbt_586637729302847337.jpeg",
      objectPosition: "center"
    },
    {
      title: "Sink & Disposal Cleaning",
      beforeImage: "https://customer-assets.emergentagent.com/job_family-cleaning-kc/artifacts/0qzwb4dm_5999047603297935839.jpeg",
      afterImage: "https://customer-assets.emergentagent.com/job_family-cleaning-kc/artifacts/1dk8ajrd_2130664415246617435.jpeg",
      objectPosition: "center"
    },
    {
      title: "Range Hood Filter",
      beforeImage: "https://customer-assets.emergentagent.com/job_family-cleaning-kc/artifacts/ih3u9sc9_20260507_153207.jpeg",
      afterImage: "https://customer-assets.emergentagent.com/job_family-cleaning-kc/artifacts/2s3vy3ve_20260507_153446.jpeg",
      objectPosition: "center"
    },
    {
      title: "Shower Floor & Grout",
      beforeImage: "https://customer-assets.emergentagent.com/job_family-cleaning-kc/artifacts/6nni3xf8_20260511_082258.jpeg",
      afterImage: "https://customer-assets.emergentagent.com/job_family-cleaning-kc/artifacts/2nequibg_20260511_094148.jpeg",
      objectPosition: "center"
    },
    {
      title: "Bathroom Tile Floor",
      beforeImage: "https://customer-assets-wrfwihn1.emergentagent.net/job_family-cleaning-kc/artifacts/kkwkyw77_17492.webp",
      afterImage: "https://customer-assets-wrfwihn1.emergentagent.net/job_family-cleaning-kc/artifacts/8vqsp34z_17495.webp",
      objectPosition: "center"
    }
  ];

  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
      {comparisons.map((item, index) => (
        <motion.div
          key={item.title}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1 }}
          viewport={{ once: true }}
        >
          <BeforeAfterSlider
            beforeImage={item.beforeImage}
            afterImage={item.afterImage}
            title={item.title}
            objectPosition={item.objectPosition}
          />
        </motion.div>
      ))}
    </div>
  );
};

export default BeforeAfterGallery;
